import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useAppState, type AppSnapshot } from '../../app/state';
import { callAction } from '../../app/actions';
import { registerHandle } from '../../app/handles';
import { NodeChip } from '../../components/NodeChip';
import { buildMenu, type MenuFacts, type MenuItem, type MenuSubItem, type MenuTarget, type NodeFacts } from '../../lib/contextMenu';
import { BRANCH_COLORS, DEFAULT_COLOR } from '../../lib/color';
import { DOC_ID_FIELD_BY_KEY, DOC_KINDS, docPermOnNode, kindColorOf, kindLabelOf } from '../../lib/docs';
import { anyExpanded, buildGraph } from '../../lib/graph';
import { inEditMode } from '../../lib/mode';
import {
  canAssignTags,
  canDocBtn,
  canEditLinks,
  canEditMap,
  canEditTodos,
  canEnterEdit,
  canViewTodos,
} from '../../lib/permissions';
import { canAssign, canEditDoc } from '../../lib/responsibility';
import { DOC_COLORS } from '../../theme/tokens';
import type { DocId, DocPerm, NodeId } from '../../lib/types';

/**
 * THE RIGHT-CLICK MENU (V0.54) — one floating card, five things it can be opened on.
 *
 * WHAT IT OFFERS IS NOT DECIDED HERE. `lib/contextMenu.ts` holds the arrangement the user
 * chose, as a pure function of facts; this region reads those facts out of the live snapshot,
 * draws what comes back, and runs what is clicked. So the menu follows the state while it is
 * open — tick Dotted links and the tick moves — and a rule about who sees what can be tested
 * without a browser.
 *
 * WHO OPENS IT: the map's own pointer handling (`MapStage`) for a node, a pill, a dotted link
 * or the empty map, and the minimap's card for itself — each calls `openContextMenu`, which
 * reaches this region through its handle. A Shift+right-click is left to the browser, so its
 * own menu (Inspect, copy text) is always one modifier away.
 *
 * HOW IT LOOKS is the `.linkmenu` floating card the dotted link's menu already wore: a panel,
 * a hairline, a soft shadow; the node it was opened on as a chip at the top (`NodeChip`, THE
 * one node-shaped chip); an icon and a gesture hint per row; a line between groups. The host
 * `#ctxmenu` sits at the top level of the page, never inside a bar: a `backdrop-filter` is a
 * containing block for `position: fixed` (why `ReslugButton` portals), and the card is fixed.
 *
 * IT CLOSES on a click anywhere outside it, Escape, a wheel notch, a resize, the window losing
 * focus, and after any item runs. Escape is stopped here, so it never also closes the popup
 * underneath (`chrome.ts`'s Escape) — one key, one thing.
 */

interface Open {
  target: MenuTarget;
  x: number;
  y: number;
}

/** A link's colour, as the link menu has always read it: a stored hex, or the default blue. */
const linkColour = (c: unknown): string => (typeof c === 'string' && /^#[0-9a-fA-F]{6}$/.test(c) ? c : DEFAULT_COLOR);

/** The header chip's colour for a page: the kind's, as a hex (`nodeTint` needs one). */
const PAGE_HEX: Record<DocPerm, string> = {
  documentation: DOC_COLORS.doc,
  internal: DOC_COLORS.internal,
  course: DOC_COLORS.course,
  demo: DOC_COLORS.demo,
  custom: '#8b95a3',
};

/** Everything `buildMenu` needs, read from the snapshot. */
function factsFor(s: AppSnapshot, target: MenuTarget): MenuFacts {
  const perm = s.perm;
  const editing = inEditMode(s);
  const graph = buildGraph(s.edges);
  const kids = (id: NodeId) => graph.childrenOf(id);
  const facts: MenuFacts = {
    editing,
    may: {
      enterEdit: canEnterEdit(perm),
      editMap: canEditMap(perm),
      editLinks: canEditLinks(perm),
      assignTags: canAssignTags(perm),
      viewTodos: canViewTodos(perm),
      editTodos: canEditTodos(perm),
    },
    map: {
      anyFolded: Object.keys(s.nodes).some((id) => s.nodes[id].collapsed && !s.nodes[id].root && kids(id).length > 0),
      anyOpen: anyExpanded(s.nodes, s.edges),
      showLinks: s.showLinks,
      docsPinned: s.docsPinned,
      heatmapOn: s.heatmapOn,
      minimapOn: s.minimapOn,
      filterActive: s.searchQuery !== '' || s.activeTags.size > 0 || !!s.activeDocStatus,
      realignOk: !s.mapFiltered,
    },
    minimapBright: s.minimapBright,
    clipboard: s.clipboard ? { label: s.clipboard.label, count: s.clipboard.count } : null,
    undoLabel: s.undoLabel,
  };

  if (target.kind === 'node') {
    const n = s.nodes[target.nodeId];
    if (n) {
      const id = n.id;
      const beneath = graph.descendants(id);
      const mayCreate = canEditMap(perm) || canAssign(s, id);
      const missingKinds = mayCreate
        ? DOC_KINDS.filter((k) => !n[DOC_ID_FIELD_BY_KEY[k.key]] && canDocBtn(perm, k.perm)).map((k) => ({
            arg: k.perm,
            label: k.label,
            color: kindColorOf(k.perm),
          }))
        : [];
      const editableDocs: NodeFacts['editableDocs'][number][] = [];
      for (const k of DOC_KINDS) {
        const docId = n[DOC_ID_FIELD_BY_KEY[k.key]] as DocId | null | undefined;
        if (docId && canDocBtn(perm, k.perm) && canEditDoc(s, docId, s.currentLocale)) {
          editableDocs.push({ arg: docId, label: k.label, color: kindColorOf(k.perm) });
        }
      }
      if (canDocBtn(perm, 'custom')) {
        for (const docId of n.customDocs ?? []) {
          if (canEditDoc(s, docId, s.currentLocale)) {
            editableDocs.push({ arg: docId, label: s.docTitles[docId] || 'Untitled', color: kindColorOf('custom') });
          }
        }
      }
      facts.node = {
        id,
        label: n.label,
        color: n.color || DEFAULT_COLOR,
        root: !!n.root,
        collapsed: !!n.collapsed,
        hasChildren: kids(id).length > 0,
        descendantCount: beneath.length,
        foldedBeneath: [id, ...beneath].some((i) => s.nodes[i]?.collapsed && !s.nodes[i]?.root && kids(i).length > 0),
        isProject: !!n.project,
        favourite: s.myItems.loaded
          ? s.myItems.favorites.some((f) => f.targetType === 'node' && f.targetId === id)
          : null,
        following: s.subscriptionsLoaded ? s.subscriptions.has(`node:${id}`) : null,
        missingKinds,
        editableDocs,
        // A copy is written from the rows as they are at Paste, so it may land anywhere —
        // even under its own original.
        pasteOk: !!s.clipboard,
      };
    }
  } else if (target.kind === 'link') {
    const lk = s.links[target.index];
    const a = lk && s.nodes[lk[0]];
    const b = lk && s.nodes[lk[1]];
    if (lk && a && b) facts.link = { aLabel: a.label, bLabel: b.label, color: linkColour(lk[2]) };
  } else if (target.kind === 'pill') {
    const n = s.nodes[target.nodeId];
    if (n) {
      const perm2 = docPermOnNode(n, target.docId);
      facts.pill = {
        kindLabel: perm2 === 'custom' ? s.docTitles[target.docId] || 'Untitled' : kindLabelOf(perm2),
        nodeLabel: n.label,
        color: PAGE_HEX[perm2],
        canEdit: editing && canEditDoc(s, target.docId, s.currentLocale),
      };
    }
  }
  return facts;
}

/** Run one item. Every verb is an action; this is only the switchboard. */
function run(target: MenuTarget, item: MenuItem, arg: string | undefined, snap: AppSnapshot): void {
  switch (target.kind) {
    case 'node': {
      const id = target.nodeId;
      switch (item.id) {
        case 'open': return void callAction('focusPopup', id);
        case 'expand': return void callAction('toggleCollapsed', id);
        case 'expandBeneath': return void callAction('expandBeneath', id);
        case 'focus': return void callAction('collapseAllBut', id);
        case 'frame': return void callAction('frameBranch', id);
        case 'favourite': return void callAction('toggleFavorite', 'node', id);
        case 'follow': return void callAction('toggleSubscription', 'node', id);
        case 'tasks': return void callAction('openNodeTasks', id, false);
        case 'addTask': return void callAction('openNodeTasks', id, true);
        case 'addChild': return void callAction('addChild', id);
        case 'addSibling': return void callAction('addSibling', id);
        case 'rename': return void callAction('beginRename', id);
        case 'colour': return void (arg && callAction('setNodeColour', id, arg));
        case 'link': return void callAction('startLinkFrom', id);
        case 'move': return void callAction('startMove', id);
        case 'copy': return void callAction('copyNode', id, false);
        case 'copyBranch': return void callAction('copyNode', id, true);
        case 'duplicate': return void callAction('duplicateNode', id);
        case 'paste': return void callAction('pasteInto', id);
        case 'createDoc': return void ((arg ?? item.arg) && callAction('createDocForNode', id, arg ?? item.arg));
        case 'editDoc': return void ((arg ?? item.arg) && callAction('openDocEditor', arg ?? item.arg, id));
        case 'tags': return void callAction('openNodeTags', id);
        case 'undo': return void callAction('performUndo');
        case 'delete': return void callAction('delNode', id);
      }
      return;
    }
    case 'map':
      switch (item.id) {
        case 'addHere': return void callAction('createNodeAt', target.x, target.y - 21, target.x < 0 ? -1 : 1);
        case 'pasteHere': return void callAction('pasteAt', target.x, target.y);
        case 'fit': return void callAction('fit');
        case 'expandAll': return void callAction('expandAll');
        case 'collapseAll': return void callAction('collapseAll');
        case 'showLinks': return void callAction('toggleLinks');
        case 'docsPinned': return void callAction('toggleDocsPinned');
        case 'heatmap': return void callAction('toggleHeatmap');
        case 'minimap': return void callAction('toggleMinimap');
        case 'clearFilters': return void callAction('clearFilters');
        case 'realign': return void callAction('realign');
        case 'editMode': return void callAction('toggleEditing');
        case 'undo': return void callAction('performUndo');
      }
      return;
    case 'link': {
      const lk = snap.links[target.index];
      if (!lk) return;
      switch (item.id) {
        case 'goToA': return void callAction('goToNode', lk[0]);
        case 'goToB': return void callAction('goToNode', lk[1]);
        case 'linkColour': return void (arg && callAction('setLinkColour', target.index, arg));
        case 'removeLink': return void callAction('removeLink', target.index);
        case 'hideLinks': return void (snap.showLinks && callAction('toggleLinks'));
      }
      return;
    }
    case 'pill':
      switch (item.id) {
        case 'openDoc': return void callAction('openNodeDoc', target.nodeId, target.docId);
        case 'openDocNewTab': return void callAction('openDocInNewTab', target.docId);
        case 'editDocPage': return void callAction('openDocEditor', target.docId, target.nodeId);
      }
      return;
    case 'minimap':
      switch (item.id) {
        case 'minimapFit': return void callAction('fit');
        case 'minimapBright': return void callAction('setMinimapBright', !snap.minimapBright);
        case 'minimapHide': return void (snap.minimapOn && callAction('toggleMinimap'));
      }
  }
}

/** The address a Copy link item puts on the clipboard, or null when there is none to give. */
function addressFor(target: MenuTarget, item: MenuItem): string | null {
  if (item.id === 'copyLink' && target.kind === 'node') return callAction('nodeAddress', target.nodeId) as string | null;
  if (item.id === 'copyDocLink' && target.kind === 'pill') return callAction('docAddress', target.docId) as string | null;
  return null;
}

/** Keep a box of this size on screen, flipping to the other side of the point when it would not fit. */
function placeAt(x: number, y: number, w: number, h: number): { left: number; top: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = x + w > vw - 8 ? x - w : x;
  let top = y + h > vh - 8 ? y - h : y;
  left = Math.max(8, Math.min(left, vw - 8 - w));
  top = Math.max(8, Math.min(top, vh - 8 - h));
  return { left, top };
}

const Icon = ({ name, className }: { name: string; className?: string }) => (
  <svg className={className ? `ico ${className}` : 'ico'} aria-hidden="true">
    <use href={`#i-${name}`} />
  </svg>
);

export function ContextMenu({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [open, setOpen] = useState<Open | null>(null);
  /** Which row's submenu is showing, by index into the entries. */
  const [subAt, setSubAt] = useState<number | null>(null);
  /** A Copy link row that has just copied — it says so for a moment before the card goes. */
  const [copied, setCopied] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [subPos, setSubPos] = useState<{ left: number; top: number } | null>(null);

  const close = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setOpen(null);
    setSubAt(null);
    setCopied(null);
    setPos(null);
    setSubPos(null);
  }, []);

  useEffect(() => {
    registerHandle('contextMenu', {
      open: (target: MenuTarget, x: number, y: number) => {
        setSubAt(null);
        setCopied(null);
        setPos(null);
        setOpen({ target, x, y });
      },
      close,
    });
  }, [close]);

  const menu = open ? buildMenu(open.target, factsFor(s, open.target)) : null;
  const empty = !!open && (!menu || menu.entries.length === 0);

  /* A class on the host while a card is up — the hook a spec or a style can key on. */
  useEffect(() => {
    host.classList.toggle('open', !!open && !empty);
  }, [host, open, empty]);

  /* An empty menu is no menu: nothing is drawn and the next click is an ordinary click. */
  useEffect(() => {
    if (empty) close();
  }, [empty, close]);

  /* Place the card once it has a size — before paint, so it never flashes at the wrong spot. */
  useLayoutEffect(() => {
    if (!open || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    setPos(placeAt(open.x, open.y, r.width, r.height));
  }, [open, menu?.entries.length]);

  /* Keyboard focus goes to the card, so the arrows and Escape are ours from the first key. */
  useEffect(() => {
    if (open && pos) cardRef.current?.focus({ preventScroll: true });
  }, [open, pos !== null]);

  /* The submenu sits beside its row, on whichever side has room. */
  useLayoutEffect(() => {
    if (subAt === null || !subRef.current || !cardRef.current) return;
    const row = rowRefs.current[subAt];
    if (!row) return;
    const card = cardRef.current.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    const w = subRef.current.offsetWidth;
    const h = subRef.current.offsetHeight;
    const right = card.right + 4;
    const left = right + w > window.innerWidth - 8 ? card.left - w - 4 : right;
    setSubPos({ left: Math.max(8, left), top: Math.max(8, Math.min(r.top - 6, window.innerHeight - 8 - h)) });
  }, [subAt]);

  /* Everything that ends it from outside. */
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (t && (cardRef.current?.contains(t) || subRef.current?.contains(t))) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        close();
      }
    };
    // A wheel over the MAP moves what the menu was opened on, so the menu goes; a wheel over the
    // menu itself scrolls a long one in a short window.
    const onWheel = (e: WheelEvent) => {
      const t = e.target as Node | null;
      if (t && (cardRef.current?.contains(t) || subRef.current?.contains(t))) return;
      close();
    };
    window.addEventListener('pointerdown', outside, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
    };
  }, [open, close]);

  if (!open || !menu || empty) return null;
  const entries = menu.entries;
  const target = open.target;

  const choose = (item: MenuItem, index: number, arg?: string) => {
    if ((item.sub || item.swatches !== undefined) && arg === undefined) {
      setSubAt(index);
      return;
    }
    const address = addressFor(target, item);
    if (address !== null || item.id === 'copyLink' || item.id === 'copyDocLink') {
      if (!address) return close();
      void (callAction('copyText', address) as Promise<boolean>).then((ok) => {
        if (!ok) window.prompt('Copy this address:', address);
        setCopied(ok ? 'Link copied' : null);
        setTimeout(close, ok ? 650 : 0);
      });
      return;
    }
    const snap = s;
    close();
    run(target, item, arg, snap);
  };

  /* The arrows walk the rows; Right opens a submenu, Left or Escape leaves it. */
  const onCardKey = (e: ReactKeyboardEvent<HTMLDivElement>, inSub: boolean) => {
    const box = inSub ? subRef.current : cardRef.current;
    if (!box) return;
    const rows = [...box.querySelectorAll<HTMLElement>('.cm-item, .cm-sw')];
    const at = rows.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => rows[(i + rows.length) % rows.length]?.focus();
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at < 0 ? rows.length - 1 : at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(rows.length - 1);
    else if (e.key === 'ArrowRight' && !inSub) {
      const i = rowRefs.current.findIndex((r) => r === document.activeElement);
      const it = i >= 0 ? entries[i] : null;
      if (it && it.type === 'item' && (it.sub || it.swatches !== undefined)) {
        setSubAt(i);
        requestAnimationFrame(() => subRef.current?.querySelector<HTMLElement>('.cm-item, .cm-sw')?.focus());
      } else return;
    } else if ((e.key === 'ArrowLeft' || e.key === 'Escape') && inSub) {
      const back = subAt;
      setSubAt(null);
      if (back !== null) rowRefs.current[back]?.focus();
    } else if (e.key === 'Tab') {
      // A menu is one stop: Tab leaves it rather than walking out of it row by row.
      close();
      return;
    } else return;
    e.preventDefault();
    e.stopPropagation();
  };

  const subItem = subAt !== null && entries[subAt]?.type === 'item' ? (entries[subAt] as MenuItem) : null;

  const card = (
    <>
      <div
        ref={cardRef}
        className="ctxmenu"
        id="ctxmenu-card"
        role="menu"
        tabIndex={-1}
        data-target={target.kind}
        style={pos ? { left: pos.left, top: pos.top } : { left: open.x, top: open.y, visibility: 'hidden' }}
        onKeyDown={(e) => onCardKey(e, false)}
        onContextMenu={(e) => e.preventDefault()}
      >
        {menu.header ? (
          <div className="cm-head">
            {menu.header.color || menu.header.root ? (
              <NodeChip node={{ label: menu.header.label, color: menu.header.color, root: menu.header.root }} isLight={s.isLight} />
            ) : (
              <div className="cm-title">{menu.header.label}</div>
            )}
          </div>
        ) : null}
        {entries.map((e, i) => {
          if (e.type === 'separator') return <div key={`sep-${i}`} className="cm-sep" role="separator" />;
          const opens = !!e.sub || e.swatches !== undefined;
          const label = copied && (e.id === 'copyLink' || e.id === 'copyDocLink') ? copied : e.label;
          return (
            <button
              key={e.id}
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              type="button"
              role={e.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
              aria-checked={e.checked === undefined ? undefined : e.checked}
              aria-haspopup={opens ? 'menu' : undefined}
              aria-expanded={opens ? subAt === i : undefined}
              className={['cm-item', e.danger ? 'danger' : '', subAt === i ? 'open' : ''].filter(Boolean).join(' ')}
              data-item={e.id}
              onClick={() => choose(e, i)}
              onMouseEnter={() => {
                if (hoverTimer.current) clearTimeout(hoverTimer.current);
                hoverTimer.current = setTimeout(() => setSubAt(opens ? i : null), opens ? 110 : 160);
              }}
            >
              <Icon name={copied && label === copied ? 'check' : e.icon} />
              <span className="cm-lbl">{label}</span>
              {e.keys ? <span className="cm-keys">{e.keys}</span> : null}
              {e.checked ? <Icon name="check" className="cm-check" /> : null}
              {opens ? <Icon name="chev" className="cm-trail" /> : null}
            </button>
          );
        })}
      </div>
      {subItem ? (
        <div
          ref={subRef}
          className="ctxmenu sub"
          id="ctxmenu-sub"
          role="menu"
          tabIndex={-1}
          style={subPos ? { left: subPos.left, top: subPos.top } : { visibility: 'hidden', left: 0, top: 0 }}
          onKeyDown={(e) => onCardKey(e, true)}
          onContextMenu={(e) => e.preventDefault()}
          onMouseEnter={() => {
            if (hoverTimer.current) clearTimeout(hoverTimer.current);
          }}
        >
          {subItem.swatches !== undefined ? (
            <div className="cm-swatches">
              {BRANCH_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={c.toLowerCase() === (subItem.swatches ?? '').toLowerCase() ? 'cm-sw on' : 'cm-sw'}
                  style={{ background: c }}
                  aria-label={`Colour ${c}`}
                  title={c}
                  data-colour={c}
                  onClick={() => choose(subItem, subAt!, c)}
                />
              ))}
            </div>
          ) : (
            (subItem.sub ?? []).map((row: MenuSubItem) => (
              <button
                key={row.arg}
                type="button"
                role="menuitem"
                className="cm-item"
                data-item={subItem.id}
                data-arg={row.arg}
                onClick={() => choose(subItem, subAt!, row.arg)}
              >
                {row.dot ? <span className="cm-dot" style={{ background: row.dot }} /> : <Icon name={subItem.icon} />}
                <span className="cm-lbl">{row.label}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </>
  );

  return card;
}
